import * as yup from 'yup';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  LinearProgress,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import React, { useCallback, useEffect, useState } from 'react';
import { useCandidateContext } from "../../providers/CandidateProvider";

import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import api from "../../api/axiosInstance";
import { yupResolver } from '@hookform/resolvers/yup';

const URL_PATTERNS = {
  GENERAL: /^https?:\/\/[a-zA-Z0-9.-]+(\.[a-zA-Z]{2,}){1,}([/\S]*)?$/,
  LINKEDIN: /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/
};

const PORTFOLIO_FIELDS = [
  {
    id: 'linkedin',
    label: 'LinkedIn',
    placeholder: 'https://www.linkedin.com/in/your-profile',
    fieldName: 'linkedinUrl',
    required: true
  },
  {
    id: 'personalWebsite',
    label: 'Personal Website',
    placeholder: 'https://your-website.com',
    fieldName: 'personalWebsiteUrl',
    required: false
  },
  {
    id: 'figma',
    label: 'Figma',
    placeholder: 'https://www.figma.com/your-profile',
    fieldName: 'figmaUrl',
    required: false
  },
  {
    id: 'github',
    label: 'Github',
    placeholder: 'https://github.com/your-username',
    fieldName: 'githubUrl',
    required: false
  }
];

const FORM_DEFAULTS = {
  linkedinUrl: '',
  personalWebsiteUrl: '',
  figmaUrl: '',
  githubUrl: ''
};

const FORM_MESSAGES = {
  LINKEDIN_ERROR: 'Enter a valid LinkedIn profile URL (e.g., https://www.linkedin.com/in/your-name)',
  URL_ERROR: 'Enter a valid website URL (must start with http:// or https://)',
  HELPER_TEXT: '* LinkedIn is required. Other fields are optional but recommended.'
};

const portfolioSchema = yup.object().shape({
  linkedinUrl: yup
    .string()
    .matches(URL_PATTERNS.LINKEDIN, FORM_MESSAGES.LINKEDIN_ERROR)
    .required('LinkedIn URL is required'),
  personalWebsiteUrl: yup
    .string()
    .matches(URL_PATTERNS.GENERAL, FORM_MESSAGES.URL_ERROR)
    .nullable()
    .transform((value) => value === '' ? null : value),
  figmaUrl: yup
    .string()
    .matches(URL_PATTERNS.GENERAL, FORM_MESSAGES.URL_ERROR)
    .nullable()
    .transform((value) => value === '' ? null : value),
  githubUrl: yup
    .string()
    .matches(URL_PATTERNS.GENERAL, FORM_MESSAGES.URL_ERROR)
    .nullable()
    .transform((value) => value === '' ? null : value)
});

const usePortfolioForm = (initialData) => {
  return useForm({
    resolver: yupResolver(portfolioSchema),
    mode: 'onChange',
    defaultValues: initialData
  });
};

const apiService = {
  savePortfolio: async (data) => {
    const serverUrl = process.env.REACT_APP_SERVER_URL;

    if (!serverUrl) {
      throw new Error('API configuration is missing. Please contact support.');
    }

    const body = {
      linkedIn: data.linkedinUrl || '',
      github: data.githubUrl || '',
      personalWebsite: data.personalWebsiteUrl || '',
      figma: data.figmaUrl || '',
      onBoardingComplete: true,
    };

    try {
      const response = await api.patch(
        "/candidates/me",
        body,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to save portfolio. Please try again.';
      throw new Error(msg);
    }
  }
};

const FormHeader = () => (
  <Box sx={{ textAlign: 'center', mb: 4 }}>
    <Typography 
      variant="h3" 
      sx={{ 
        fontFamily: 'Readex Pro, sans-serif',
        fontWeight: 500,
        fontSize: '20px',
        lineHeight: '30px',
        letterSpacing: '0%',
        textAlign: 'center',
        color: '#000000',
        mb: 1
      }}
    >
      Portfolio
    </Typography>
    <Typography 
      variant="body1" 
      sx={{ 
        fontFamily: 'Inter, sans-serif',
        fontWeight: 300,
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0%',
        textAlign: 'center',
        color: '#000000'
      }}
    >
      Please provide the following links.
    </Typography>
  </Box>
);

const PortfolioField = ({ field, control, errors }) => (
  <Controller
    key={field.id}
    name={field.fieldName}
    control={control}
    render={({ field: fieldProps }) => (
      <TextField
        {...fieldProps}
        fullWidth
        label={field.label}
        placeholder={field.placeholder}
        variant="outlined"
        size="small"
        type="url"
        required={field.required}
        InputLabelProps={{ shrink: true }}
        error={!!errors[field.fieldName]}
        helperText={errors[field.fieldName]?.message}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1,
            fontSize: '14px',
            '& fieldset': {
              borderColor: '#d0d0d0',
            },
            '&:hover fieldset': {
              borderColor: '#999',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FF5722',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '14px',
          }
        }}
      />
    )}
  />
);

const HelperText = () => (
  <Typography 
    variant="caption" 
    sx={{ 
      color: '#666',
      fontSize: '12px',
      fontStyle: 'italic',
      mt: -2
    }}
  >
    {FORM_MESSAGES.HELPER_TEXT}
  </Typography>
);

const FormActions = ({ 
  onBack, 
  isValid, 
  isSubmitting 
}) => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    mt: 2,
  }}>
    <Button
      variant="outlined"
      startIcon={<ArrowBackIcon />}
      onClick={onBack}
      disabled={isSubmitting}
      size="small"
      sx={{
        color: '#666',
        borderColor: '#d0d0d0',
        px: 3,
        py: 1,
        fontWeight: 'bold',
        fontSize: '12px',
        borderRadius: 1,
        textTransform: 'uppercase',
        '&:hover': {
          borderColor: '#999',
          backgroundColor: '#f8f8f8'
        },
        '&:disabled': {
          opacity: 0.4
        }
      }}
    >
      BACK
    </Button>

    <Button
      type="submit"
      variant="contained"
      disabled={!isValid || isSubmitting}
      size="small"
      sx={{
        backgroundColor: '#FF5722',
        color: 'white',
        px: 3,
        py: 1,
        fontWeight: 'bold',
        fontSize: '12px',
        borderRadius: 1,
        textTransform: 'uppercase',
        boxShadow: '0 2px 8px rgba(255, 87, 34, 0.3)',
        '&:hover': {
          backgroundColor: '#E64A19',
          boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)',
          transform: 'translateY(-1px)'
        },
        '&:disabled': {
          backgroundColor: '#ccc',
          boxShadow: 'none'
        }
      }}
    >
      {isSubmitting ? (
        <>
          <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
          FINISHING...
        </>
      ) : (
        'FINISH'
      )}
    </Button>
  </Box>
);

const StatusAlert = ({ saveStatus, onClose }) => {
  if (!saveStatus) return null;

  return (
    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
      <Alert 
        severity={saveStatus.type} 
        sx={{ width: '512px' }}
        onClose={onClose}
        action={
          saveStatus.type === 'error' && saveStatus.retry ? (
            <Button color="inherit" size="small" onClick={saveStatus.retry}>
              RETRY
            </Button>
          ) : null
        }
      >
        {saveStatus.message}
      </Alert>
    </Box>
  );
};

const PortfolioForm = ({ 
  control, 
  errors, 
  handleSubmit, 
  onSubmit, 
  isValid, 
  isSubmitting,
  onBack
}) => (
  <form
    onSubmit={handleSubmit(onSubmit)}
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 600,
          fontSize: '14px',
          color: '#000000',
          mb: -1
        }}
      >
        Mandatory
      </Typography>

      <PortfolioField 
        field={PORTFOLIO_FIELDS[0]}
        control={control}
        errors={errors}
      />

      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 600,
          fontSize: '14px',
          color: '#000000',
          mb: -1
        }}
      >
        Optional professional references
      </Typography>

      {PORTFOLIO_FIELDS.slice(1).map((field) => (
        <PortfolioField 
          key={field.id}
          field={field}
          control={control}
          errors={errors}
        />
      ))}

      <HelperText />
    </Box>
    
    <FormActions
      onBack={onBack}
      isValid={isValid}
      isSubmitting={isSubmitting}
    />
  </form>
);

const Portfolio = ({ 
  formData, 
  setFormData, 
  onFinish, 
  onBack
}) => {
  const { candidate, refreshCandidate } = useCandidateContext(); 
  const [saveStatus, setSaveStatus] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [progress, setProgress] = useState(100);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize form from CandidateProvider, fallback to formData from parent
  const initialData = {
    ...FORM_DEFAULTS,
    linkedinUrl: candidate?.linkedIn || formData.linkedinUrl || '',
    personalWebsiteUrl: candidate?.personalWebsite || formData.personalWebsiteUrl || '',
    figmaUrl: candidate?.figma || formData.figmaUrl || '',
    githubUrl: candidate?.github || formData.githubUrl || ''
  };

  const form = usePortfolioForm(initialData);
  const { control, handleSubmit, watch, formState: { errors, isValid } } = form;

  // Watch for changes and update parent state (NO localStorage)
  useEffect(() => {
    const subscription = watch((watchedValues) => {
      const cleanedValues = Object.keys(watchedValues).reduce((acc, key) => {
        if (watchedValues[key] !== undefined) {
          acc[key] = watchedValues[key];
        }
        return acc;
      }, {});

      setFormData(prev => ({ ...prev, ...cleanedValues }));
    });
    
    return () => subscription.unsubscribe();
  }, [watch, setFormData]);

  useEffect(() => {
    let timer;
    if (snackbar.open && snackbar.severity === 'success') {
      setProgress(100);
      const duration = 1500;
      const interval = 30;
      const decrement = (100 / duration) * interval;
      
      timer = setInterval(() => {
        setProgress((prevProgress) => {
          const newProgress = prevProgress - decrement;
          if (newProgress <= 0) {
            clearInterval(timer);
            return 0;
          }
          return newProgress;
        });
      }, interval);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [snackbar.open, snackbar.severity]);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const onSubmit = useCallback(async (data) => {
    setIsSubmitting(true);
    setSaveStatus(null);
    
    try {
      await apiService.savePortfolio(data);
      setFormData(prev => ({ ...prev, ...data }));
      
      await refreshCandidate();
      
      setSnackbar({
        open: true,
        message: 'Profile registered successfully!',
        severity: 'success'
      });
      
      setTimeout(() => {
        onFinish();
      }, 1500);
    } catch (error) {
      setSaveStatus({ 
        type: 'error', 
        message: error.message || 'Failed to save portfolio. Please try again.',
        retry: () => {
          setSaveStatus(null);
          handleSubmit(onSubmit)();
        }
      });
      setIsSubmitting(false);
    }
  }, [setFormData, onFinish, handleSubmit, refreshCandidate]);

  return (
    <Box sx={{ 
      minHeight: 'calc(100vh - 116px)',
      backgroundColor: '#DED8D1',
      pt: 2,
      pb: 3,
      px: '80px',
    }}>
      <Container 
        maxWidth={false}
        sx={{ 
          px: 0,
          width: '100%',
          height: '100%',
        }}
      >
        <Box 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            backgroundColor: 'white',
            borderRadius: 0,
            boxShadow: 'none',
            border: 'none',
            margin: '0 auto',
            mx: 0,
            width: '100%',
            minHeight: 'calc(100vh - 116px - 32px)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <FormHeader />
          
          <StatusAlert 
            saveStatus={saveStatus} 
            onClose={() => setSaveStatus(null)} 
          />

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%'
          }}>
            <Box sx={{
              width: '512px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              <PortfolioForm
                control={control}
                errors={errors}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                isValid={isValid}
                isSubmitting={isSubmitting}
                onBack={onBack}
              />
            </Box>
          </Box>
        </Box>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 1500 : 6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 2 }}
      >
        <Box>
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            sx={{ 
              width: '100%',
              minWidth: '300px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
          {snackbar.severity === 'success' && (
            <LinearProgress 
              variant="determinate" 
              value={progress}
              sx={{
                height: 4,
                backgroundColor: 'rgba(255,255,255,0.3)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#EAE7E6'
                }
              }}
            />
          )}
        </Box>
      </Snackbar>
    </Box>
  );
};

export default Portfolio;